import React from 'react'
import classNames from 'classnames'
import { HlsFullscreenButton } from './HlsFullscreenButton'
import { HlsPipButton } from './HlsPipButton'
import { HlsPlayPauseButton } from './HlsPlayPauseButton'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsSeekBar } from './HlsSeekBar'
import { HlsSettingsButton } from './HlsSettingsButton'
import { HlsTimeDisplay } from './HlsTimeDisplay'
import { HlsVolumeControl } from './HlsVolumeControl'

interface HlsControlBarProps {
  visible: boolean
  onInteraction: () => void
}

/**
 * Bottom control bar for the HLS.js player.
 * Contains seek bar, play/pause, volume, and time display.
 * Fades in/out based on the `visible` prop.
 *
 * Layout (top to bottom):
 * - Seek bar (full width)
 * - Controls row: play/pause | volume + slider | time | ... spacer ... | PiP | fullscreen
 */
export const HlsControlBar: React.FC<HlsControlBarProps> = ({ visible, onInteraction }) => {
  const { videoRef } = useHlsPlayerUI()

  return (
    <div
      data-hls-control-bar
      className={classNames(
        'absolute inset-x-0 bottom-0 flex flex-col bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 pb-2 pt-10 transition-opacity duration-[400ms]',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      onClick={onInteraction}
    >
      {/* Seek bar */}
      <HlsSeekBar />

      {/* Controls row */}
      <div className="flex items-center gap-1.5">
        {/* Left controls */}
        <HlsPlayPauseButton />
        <HlsVolumeControl />
        <HlsTimeDisplay />

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right controls */}
        <HlsSettingsButton />
        <HlsPipButton videoRef={videoRef} />
        <HlsFullscreenButton />
      </div>
    </div>
  )
}
